import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

import { authStorage } from '../services/api';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');
const SOCKET_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

type Options = {
  userId: string;
  role: string;
  sessionId: string;
  tentativeId: string;
  enabled: boolean;
  onForceClose?: () => void;
};

export function useEvaluationSessionSocket({
  userId,
  role,
  sessionId,
  tentativeId,
  enabled,
  onForceClose,
}: Options) {
  const onForceCloseRef = useRef(onForceClose);
  onForceCloseRef.current = onForceClose;

  useEffect(() => {
    if (!enabled || !userId || !sessionId || !tentativeId) return undefined;

    const token = authStorage.getAccessToken();
    if (!token) return undefined;

    const socket: Socket = io(SOCKET_BASE_URL, { transports: ['websocket', 'polling'] });

    const authenticate = () => {
      socket.emit('authenticate', { userId, role, token });
    };

    const joinRoom = () => {
      socket.emit('join-evaluation-session', { sessionId, tentativeId });
    };

    socket.on('connect', authenticate);
    socket.on('authenticated', joinRoom);
    socket.on('evaluation-force-close', () => {
      onForceCloseRef.current?.();
    });

    const heartbeat = window.setInterval(() => {
      if (!socket.connected) return;
      socket.emit('evaluation-heartbeat', {
        sessionId,
        tentativeId,
        focused: document.visibilityState === 'visible',
      });
    }, 10_000);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible' && socket.connected) {
        socket.emit('evaluation-focus-lost', { sessionId, tentativeId });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      socket.emit('leave-evaluation-session');
      socket.off('connect', authenticate);
      socket.off('authenticated', joinRoom);
      socket.off('evaluation-force-close');
      socket.disconnect();
    };
  }, [enabled, userId, role, sessionId, tentativeId]);
}
